/**
 * PeriodSelector — SIH v1.1 §3.2 segmented control.
 *
 * The v1 markup (a `<fieldset>` of `<input type=radio>` pairs with a
 * visually-hidden `<legend>"Period"</legend>`) is preserved per UX-T16
 * §structural 3 — the form contract, arrow-key semantics, deep-link grammar,
 * and the fieldset/legend accessibility pairing all stay byte-identical. The
 * visual: the radios render as absolutely-positioned children of a sunken
 * track that gets `display: contents` so the fieldset box never appears;
 * each segment renders its label as the visible surface.
 *
 * The legend stays visually-hidden but reachable to assistive tech (so
 * screen-reader users hear "Period, group" rather than four disjoint
 * radios). Custom range fields keep their existing copy + validation
 * behaviour, restyled with `--border-strong` on the date inputs.
 */

import { useId, useState } from 'react'
import type { PeriodSelection } from '@domain/types'
import { navigateTo } from './route'

const PRESETS: ReadonlyArray<{ value: 'TODAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'CUSTOM'; label: string }> = [
  { value: 'TODAY', label: 'Today' },
  { value: 'LAST_7_DAYS', label: 'Last 7 days' },
  { value: 'LAST_30_DAYS', label: 'Last 30 days' },
  { value: 'CUSTOM', label: 'Custom range' },
]

export function PeriodSelector({ period, pathname }: { period: PeriodSelection; pathname: string }) {
  const [draftFrom, setDraftFrom] = useState(period.preset === 'CUSTOM' ? (period.from ?? '') : '')
  const [draftTo, setDraftTo] = useState(period.preset === 'CUSTOM' ? (period.to ?? '') : '')
  const [rejection, setRejection] = useState<string | null>(null)
  const fromId = useId()
  const toId = useId()

  const applyCustom = () => {
    const from = draftFrom
    const to = draftTo
    // Inline validation BEFORE any navigation/query (FR-5 fit criterion).
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
      setRejection('From date must be a valid date (yyyy-MM-dd).')
      return
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(to)) {
      setRejection('To date must be a valid date (yyyy-MM-dd).')
      return
    }
    if (from > to) {
      setRejection('From must be on or before To (from ≤ to).')
      return
    }
    setRejection(null)
    navigateTo(pathname, { preset: 'CUSTOM', from, to })
  }

  return (
    <div className="period-selector" data-testid="period-selector">
      {/* UX-T16 §structural 3 — Period keeps the native <fieldset> +
       * visually-hidden <legend> so screen readers hear "Period, group"
       * around the four radios. The fieldset's default box is removed
       * via `display: contents` in styles.css; the radios render
       * inside a `<div class="period-segmented">` visual track so the
       * v1 testid surface (period-today, period-last_7_days, ...) stays
       * intact and the NFR-U1 journey stays green unmodified. */}
      <fieldset
        className="period-presets"
        role="radiogroup"
        aria-label="Period"
        data-testid="period-segmented"
      >
        <legend className="visually-hidden">Period</legend>
        <div className="period-segmented">
          {PRESETS.map((preset) => (
            <label key={preset.value}>
              <input
                type="radio"
                name="period"
                value={preset.value}
                checked={period.preset === preset.value}
                onChange={() => {
                  setRejection(null)
                  navigateTo(pathname, { preset: preset.value })
                }}
                data-testid={`period-${preset.value.toLowerCase()}`}
              />
              <span className="segment">{preset.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {period.preset === 'CUSTOM' && (
        <div className="period-custom" data-testid="period-custom-fields">
          <div>
            <label htmlFor={fromId}>From</label>
            <input
              id={fromId}
              type="date"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              data-testid="custom-from"
            />
          </div>
          <div>
            <label htmlFor={toId}>To</label>
            <input id={toId} type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} data-testid="custom-to" />
          </div>
          <button type="button" onClick={applyCustom} data-testid="custom-apply" className="apply-button">
            Apply range
          </button>
          {rejection !== null && (
            <p role="alert" className="range-rejection" data-testid="range-rejection">
              {rejection}
            </p>
          )}
        </div>
      )}
    </div>
  )
}